import { useCallback, useState, useEffect, useRef } from "react";
import { useQuery } from "@design-lab/shared/graphql";
import { type GetCommunityImagesQuery, GetCommunityImagesDocument } from "graphql-api/operations";
import {
  useGridColumns,
  calculateGridLimit,
  calculateItemsToCompleteRow
} from "../hooks/useGridColumns";

type CommunityImage = GetCommunityImagesQuery["communityImages"][0];

type UseCommunityImagesOptions = {
  search?: string | undefined;
  sortBy?: string;
};

export function useCommunityImages(options: UseCommunityImagesOptions = {}) {
  const { search, sortBy } = options;

  const gridColumns = useGridColumns();
  const baseLimit = calculateGridLimit(gridColumns, 4); // Load 4 rows at a time

  const [allImages, setAllImages] = useState<CommunityImage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const prevOptionsRef = useRef({ search, sortBy, gridColumns });
  const isFirstRender = useRef(true);
  const initialDataProcessed = useRef(false);

  const updateHasMore = useCallback((value: boolean) => {
    hasMoreRef.current = value;
    setHasMore(value);
  }, []);

  const {
    data,
    loading: initialLoading,
    error,
    refetch,
    fetchMore
  } = useQuery(GetCommunityImagesDocument, {
    variables: {
      limit: baseLimit,
      offset: 0,
      search: search || undefined,
      sortBy: sortBy || undefined
    },
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (error) {
      console.error("GraphQL query error:", error);
    }
  }, [error]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevOptionsRef.current = { search, sortBy, gridColumns };
      return;
    }

    const prev = prevOptionsRef.current;
    const hasChanged =
      search !== prev.search || sortBy !== prev.sortBy || gridColumns !== prev.gridColumns;

    if (hasChanged) {
      setAllImages([]);
      offsetRef.current = 0;
      updateHasMore(true);
      setIsLoadingMore(false);
      initialDataProcessed.current = false;

      refetch({
        limit: baseLimit,
        offset: 0,
        search: search || undefined,
        sortBy: sortBy || undefined
      });

      prevOptionsRef.current = { search, sortBy, gridColumns };
    }
  }, [search, sortBy, gridColumns, refetch, baseLimit, updateHasMore]);

  useEffect(() => {
    if (data?.communityImages && !initialDataProcessed.current) {
      const images = data.communityImages;

      if (offsetRef.current === 0) {
        const minItemsForMore = Math.max(1, gridColumns || 4);
        const shouldHaveMore = images.length >= minItemsForMore;
        setAllImages(images as CommunityImage[]);
        offsetRef.current = images.length;
        updateHasMore(shouldHaveMore);
        initialDataProcessed.current = true;
      }
    }
  }, [data, gridColumns, updateHasMore]);

  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const currentOffset = offsetRef.current;
      const itemsToCompleteRow = calculateItemsToCompleteRow(allImages.length, gridColumns);
      const additionalRowsToLoad = itemsToCompleteRow > 0 ? 3 : 4;
      const loadLimit = itemsToCompleteRow + calculateGridLimit(gridColumns, additionalRowsToLoad);

      const result = await fetchMore({
        variables: {
          limit: loadLimit,
          offset: currentOffset,
          search: search || undefined,
          sortBy: sortBy || undefined
        }
      });

      const newImages = result.data?.communityImages ?? [];

      if (offsetRef.current === currentOffset) {
        if (newImages.length > 0) {
          setAllImages(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const uniqueNewImages = newImages.filter(
              (img: CommunityImage) => !existingIds.has(img.id)
            );
            return [...prev, ...uniqueNewImages];
          });

          offsetRef.current += newImages.length;
          updateHasMore(newImages.length >= gridColumns);
        } else {
          updateHasMore(false);
        }
      }
    } catch (loadError) {
      console.error("Failed to load more images:", loadError);
      window.Honeybadger?.notify("Failed to load more images", {
        params: {
          error: loadError instanceof Error ? loadError.message : String(loadError),
          error_object: loadError instanceof Error ? loadError.toString() : String(loadError),
          stack_trace: loadError instanceof Error ? loadError.stack : null,
          hook: "useCommunityImages",
          action: "loadMore",
          search,
          sortBy
        }
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, fetchMore, gridColumns, search, sortBy, allImages.length, updateHasMore]);

  const resetAndRefetch = useCallback(() => {
    setAllImages([]);
    offsetRef.current = 0;
    updateHasMore(true);
    setIsLoadingMore(false);
    initialDataProcessed.current = false;

    refetch({
      limit: baseLimit,
      offset: 0,
      search: search || undefined,
      sortBy: sortBy || undefined
    });
  }, [refetch, baseLimit, search, sortBy, updateHasMore]);

  return {
    communityImages: allImages,
    loading: initialLoading && allImages.length === 0,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refetch: resetAndRefetch
  };
}
